/// <amd-module name="Wit/WorkItemTFVCLinks/Components/ChangesetPicker/ChangesetPicker" />
import { TfvcChangesetRef } from "VC/Common/Contracts/Git";
import { getTfvcClient } from "VC/Common/RestClient/Tfvc";
import { useDebouncedSubscription, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import * as Resources from "Wit/WorkItemTFVCLinks/Resources";
import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { ChangesetPickerDialog } from "./ChangesetPickerDialog";

interface IChangesetPickerProps {
    onChangesetChanged: (changeset: TfvcChangesetRef | undefined, path?: string) => void;
}

export function ChangesetPicker({ onChangesetChanged }: IChangesetPickerProps) {
    const pageContext = usePageContext();
    const client = useMemo(() => getTfvcClient(pageContext), [pageContext]);
    const respondingToDialogSelection = useRef(false);
    const [changesetPickerDialogOpen, setChangesetPickerDialogOpen] = useObservable<boolean>(false);
    const [changesetIdText, setChangesetIdText] = useObservable<string>("");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const changesetPath = useRef("");

    useDebouncedSubscription(
        changesetIdText,
        300,
        async newChangesetIdText => {
            if (respondingToDialogSelection.current) {
                // Avoid recursion
                return;
            }

            if (!newChangesetIdText) {
                onChangesetChanged(undefined);
                setErrorMessage(Resources.ErrorMissingChangesetId);
                return;
            }

            const changesetId = parseInt(newChangesetIdText);
            if (!Number.isFinite(changesetId)) {
                onChangesetChanged(undefined);
                setErrorMessage(Resources.ErrorInvalidChangesetId);
                return;
            }

            const changeset = await client.getChangeset(changesetId).catch(() => undefined);
            if (newChangesetIdText !== changesetIdText.value) {
                // The input has been changed since this request fired; do nothing with the response.
                return;
            }

            if (!changeset) {
                onChangesetChanged(undefined);
                setErrorMessage(format(Resources.ErrorCannotFindChangeset, newChangesetIdText));
                return;
            }

            onChangesetChanged(changeset, changesetPath.current);
            setErrorMessage(undefined);
        },
        [client]
    );

    const selectChangesets = useCallback((changeset: TfvcChangesetRef, path: string) => {
        respondingToDialogSelection.current = true;
        changesetIdText.value = changeset.changesetId.toString();
        setChangesetPickerDialogOpen(false);
        changesetPath.current = path;
        onChangesetChanged(changeset, changesetPath.current);
        respondingToDialogSelection.current = false;
    }, []);

    return (
        <>
            <FormItem className="changeset-picker-form-item" label={Resources.ChangesetIdLabel} error={!!errorMessage} message={errorMessage}>
                <div className="flex-row flex-wrap gap-8">
                    <TextField containerClassName="flex-grow" value={changesetIdText} onChange={(e, val) => setChangesetIdText(val)} />
                    <Button text={Resources.FindChangesets} onClick={() => setChangesetPickerDialogOpen(true)} />
                </div>
            </FormItem>

            <ConditionalChildren renderChildren={changesetPickerDialogOpen}>
                <ChangesetPickerDialog onChangesetChosen={selectChangesets} onDismiss={() => setChangesetPickerDialogOpen(false)} />
            </ConditionalChildren>
        </>
    );
}
